import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Compass } from "lucide-react";
import useReveal from "@/hooks/useReveal";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * ExplorarPorCategoria, chips clicáveis com as categorias temáticas.
 * Consome /api/category-list (SEO categories) e filtra as meta-categorias
 * (sermoes, devocionais) para deixar somente as temáticas.
 */
const META_SLUGS = new Set(["sermoes", "devocionais"]);

// Título curto para chip (a partir do title completo)
function shortLabel(title) {
  return title
    .replace(/de Carlos Medeiros/i, "")
    .replace(/Cristã[o]?/i, "")
    .replace(/Cristão/i, "")
    .replace(/Diários?/i, "")
    .replace(/\s+/g, " ")
    .trim();
}

export default function ExplorarPorCategoria() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const r = useReveal();

  useEffect(() => {
    api
      .get("/category-list")
      .then(({ data }) => {
        const list = (data?.items || []).filter(
          (it) => !META_SLUGS.has(it.slug)
        );
        setItems(list);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !items.length) return null;

  return (
    <section
      id="categorias"
      data-testid="categorias-section"
      className="relative bg-white py-16 md:py-20 border-t border-[#0B1E36]/6"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-8">
        <div ref={r} className="reveal">
          <div className="flex items-center gap-3 mb-6">
            <Compass size={13} strokeWidth={1.6} className="text-[#C5A059]" />
            <span className="text-[0.68rem] uppercase tracking-[0.32em] text-[#C5A059] font-medium">
              Explorar por tema
            </span>
          </div>
          <h2 className="font-serif-display text-[#0B1E36] text-[1.85rem] md:text-[2.35rem] lg:text-[2.75rem] leading-[1.08] tracking-[-0.02em] font-medium max-w-3xl">
            Encontre a Palavra que{" "}
            <span className="italic text-[#C5A059]">o seu momento pede</span>.
          </h2>
          <p className="mt-4 text-[0.98rem] md:text-[1.05rem] text-[#1F1A17]/60 leading-[1.75] font-light max-w-xl">
            Percorra as temáticas pastorais e leia no seu ritmo. Sem cobrança,
            no tempo do coração.
          </p>

          <div
            data-testid="categorias-chips"
            className="mt-10 flex flex-wrap gap-3"
          >
            {items.map((it) => (
              <Link
                key={it.slug}
                to={`/categoria/${it.slug}`}
                data-testid={`categoria-chip-${it.slug}`}
                onClick={() =>
                  track("category_open", {
                    slug: it.slug,
                    source: "home_categorias",
                  })
                }
                className="group inline-flex items-center gap-2 border border-[#0B1E36]/15 hover:border-[#C5A059] px-5 py-2.5 text-[0.82rem] text-[#0B1E36] hover:text-[#C5A059] transition-colors"
              >
                <span>{shortLabel(it.title)}</span>
                <ArrowUpRight
                  size={13}
                  strokeWidth={1.6}
                  className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
